import { Col, Input, Row } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { translate } from '@portal/services/i18n';

import { useDispatch } from 'react-redux';
import CollapsibleTable from '@portal/components/CollapsibleTable/CollapsibleTable';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import Header from '@portal/components/Header/Header';
import { createGroup } from '@portal/actions/group';
import useReduxState from '@portal/hooks/useReduxState';
import { getRouteStackPath } from '@portal/config/routes';
import { IS_EDIT, UNSET_CREATE_GROUP } from '@portal/actions/actionTypes';
import { ListGroup } from '@portal/models/reducers';
import * as MessageService from '~/services/message';
import { availablePermissions } from '@portal/actions/user';
import { useNavigation } from '@portal/hooks/useNavigation';

interface RowRemit {
  minLimit: number;
  maxLimit: number;
  approvers: ListGroup[];
  minApprovers: number;
}
interface Remit {
  rows: RowRemit[];
}
interface History {
  item: string;
  uid: string;
  active: boolean;
  remit?: Remit;
}
interface Data {
  name: string;
  active: boolean;
  history: History[];
}

const ProfileManagerList = () => {
  const [onFocusSearch, setOnFocusSearch] = useState<boolean>(false);
  const [searchText, setSeachText] = useState<string>('');

  const dispatch = useDispatch();

  const navigate = useNavigation();

  const {
    group: { hasCreateGroup },
    remit,
  } = useReduxState();

  const {
    user: { permissionsList },
  } = useReduxState();

  const [rows, setRows] = React.useState<Data[]>([]);

  useEffect(() => {
    dispatch(availablePermissions());
  }, [dispatch]);

  useEffect(() => {
    dispatch({
      type: IS_EDIT,
      payload: false,
    });
  }, [dispatch]);

  const rowsMemo = React.useMemo(
    () =>
      permissionsList.map((item) => ({
        item: item?.name ?? '',
        active: false,
        uid: item.value ?? '',
      })),
    [permissionsList]
  );

  const rowPayment = React.useMemo(
    () => [
      {
        uid: 'transfer',
        item: translate('PAGES.PROFILE_MANAGER.REMIT.TRANSFER'),
        active: false,
      },
      {
        uid: 'pix',
        item: translate('PAGES.PROFILE_MANAGER.REMIT.PIX'),
        active: false,
      },
      {
        uid: 'billet',
        item: translate('PAGES.PROFILE_MANAGER.REMIT.BILLET'),
        active: false,
      },
    ],
    []
  );

  const mountRoles = useCallback(() => {
    const result: string[] = [];
    rows.forEach((item) => {
      if (item.active) {
        return item.history.forEach((item) => {
          if (item.active) {
            result.push(item.uid);
          }
        });
      }
    });

    return result;
  }, [rows]);

  const handleCreateGroup = useCallback(() => {
    if (searchText.length === 0) {
      MessageService.error(
        'PAGES.PROFILE_MANAGER.CREATE_PROFILE_SCREEN.VALIDATE.REQUIRED_NAME',
        { domain: 'validation' }
      );
      return;
    }

    if (mountRoles().length === 0) {
      MessageService.error(
        'PAGES.PROFILE_MANAGER.CREATE_PROFILE_SCREEN.VALIDATE.REQUIRED_ROLES',
        { domain: 'validation' }
      );
      return;
    }

    dispatch(createGroup(searchText, mountRoles(), remit));
  }, [dispatch, mountRoles, searchText, remit]);

  useEffect(() => {
    const posRows: Data[] = [
      {
        name: translate('PAGES.PROFILE_MANAGER.SERVICES'),
        active: false,
        history: rowsMemo,
      },
      {
        name: translate('PAGES.PROFILE_MANAGER.PAYMENT'),
        history: rowPayment,
        active: false,
      },
    ];

    setRows(posRows);
  }, [rowPayment, rowsMemo]);

  useEffect(() => {
    if (hasCreateGroup) {
      setTimeout(() => {
        dispatch({
          type: UNSET_CREATE_GROUP,
        });
        navigate(getRouteStackPath('PROFILE_MANAGER', 'PROFILE_LIST'));
      }, 2000);
    }
  }, [dispatch, hasCreateGroup, navigate]);

  return (
    <form>
      <Col className="create-profile">
        <Header
          subtitle={translate(
            'PAGES.PROFILE_MANAGER.CREATE_PROFILE_SCREEN.TITLE'
          )}
        />

        <Row>
          <label
            className="create-profile__search__label"
            htmlFor="input-search"
          >
            <Input
              id="input-search"
              value={searchText}
              onChange={(e) => setSeachText(e.target.value)}
              onFocus={() => setOnFocusSearch(true)}
              onBlur={() => setOnFocusSearch(false)}
              className="create-profile__search__input"
            />

            {!onFocusSearch && !searchText && (
              <span className="create-profile__search__input-placeholder">
                {translate(
                  'PAGES.PROFILE_MANAGER.CREATE_PROFILE_SCREEN.NAME_OF_GROUP'
                )}
              </span>
            )}
          </label>
        </Row>

        <div>
          <CollapsibleTable rows={rows} setRows={setRows} />
        </div>

        <Row>
          <Col flex={1} />
          <Col style={{ marginTop: 189, marginBottom: 20 }}>
            <AdvancedButton
              height="3.438rem"
              fullWidth={true}
              type="button"
              onClick={handleCreateGroup}
              text={translate('PAGES.USER_MANAGER.CREATE_USER_SCREEN.SAVE_BTN')}
            />
          </Col>
        </Row>
      </Col>
    </form>
  );
};

export default ProfileManagerList;
